import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useConversations } from "../../features/inbox/api.js";
import { useBusiness } from "../../lib/business.js";
import { Icon, type IconName } from "./Icon.js";
import styles from "./MobileNav.module.css";

/**
 * Phone navigation (doc 01 §1.11): the left rail is hidden ≤980px, so this
 * floating bottom pill replaces it — 4 primary tabs (Inbox carries an unread
 * badge) + a "More" sheet for the rest. Ports the prototype's `MobileNav` /
 * `.cp-bottomnav` (demo-main.jsx + hillman-demo.css) — styling matched 1:1.
 * Hidden ≤980px-only via CSS, and hidden again while a conversation thread is
 * open (so it never fights the composer).
 */
const PRIMARY: { path: string; label: string; icon: IconName }[] = [
  { path: "/", label: "Dashboard", icon: "dashboard" },
  { path: "/broadcasts", label: "Broadcasts", icon: "broadcast" },
  { path: "/campaigns", label: "Campaigns", icon: "campaign" },
  { path: "/inbox", label: "Inbox", icon: "inbox" },
];
const MORE: { path: string; label: string; icon: IconName; aiGated?: boolean }[] = [
  { path: "/templates", label: "Templates", icon: "template" },
  { path: "/contacts", label: "Contacts", icon: "contacts" },
  { path: "/automations", label: "Automations", icon: "automation" },
  { path: "/ai-agent", label: "AI Agent", icon: "ai", aiGated: true },
  { path: "/settings", label: "Settings", icon: "settings" },
];

export function MobileNav({ aiEnabled }: { aiEnabled: boolean }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { activeId } = useBusiness();
  const convos = useConversations(activeId, { q: "", filter: "all" });
  const unread = convos.data?.pages[0]?.meta?.unread ?? 0;
  const { pathname } = useLocation();
  const moreItems = MORE.filter((m) => !m.aiGated || aiEnabled);
  const moreActive = moreItems.some((m) => m.path === pathname);

  return (
    <>
      <nav className={styles.bar} aria-label="Primary">
        {PRIMARY.map((it) => (
          <NavLink
            key={it.path}
            to={it.path}
            end={it.path === "/"}
            className={({ isActive }) => `${styles.item} ${isActive ? styles.active : ""}`}
          >
            <Icon name={it.icon} size={22} />
            <span className={styles.label}>{it.label}</span>
            {it.path === "/inbox" && unread > 0 && <span className={styles.badge}>{unread}</span>}
          </NavLink>
        ))}
        <button
          type="button"
          className={`${styles.item} ${moreActive || moreOpen ? styles.active : ""}`}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
        >
          <svg width={22} height={22} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <circle cx="4" cy="10" r="1.7" />
            <circle cx="10" cy="10" r="1.7" />
            <circle cx="16" cy="10" r="1.7" />
          </svg>
          <span className={styles.label}>More</span>
        </button>
      </nav>

      {moreOpen && (
        // biome-ignore lint/a11y/noStaticElementInteractions: scrim click-to-close; the sheet is role=dialog
        <div className={styles.backdrop} onMouseDown={() => setMoreOpen(false)}>
          <div
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-label="More screens"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={styles.grip} />
            <p className={styles.sheetHead}>More</p>
            <div className={styles.sheetList}>
              {moreItems.map((it) => (
                <NavLink
                  key={it.path}
                  to={it.path}
                  className={({ isActive }) =>
                    `${styles.sheetItem} ${isActive ? styles.active : ""}`
                  }
                  onClick={() => setMoreOpen(false)}
                >
                  <Icon name={it.icon} size={20} />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
